import * as R from 'ramda'
import { memo, useCallback } from 'react'
import { IconButton, Stack, styled, Typography } from '@mui/material'
import { CloseIcon } from '$common/icons/close'
import { MenuIcon } from '$common/icons/menu'
import { RemoveIcon } from '$common/icons/remove'
import { toLocale } from '$func/text'
import { exitRoomListFromSelectionMode } from '$logic/router'
import { showChatListMenu } from '$comp/modals/menus/chat-list-menu'
import {
  RoomListItem,
  WorkspaceType,
} from '$logic/room-list/room-list.react-hook'
import { getRoom } from '$logic/room'
import { getPv } from '$logic/pv'
import {
  showDeleteHallModal,
  showDeletePvModal,
  showDeleteRoomsModal,
  showDeleteWorkspaceConfirmModal,
  showLeaveHallModal,
} from '$comp/modals/confirmation-dialog'
import { hidePrivateSpace } from '$logic/private-space'
import { deleteWappRoom } from '$logic/wapp-room'
import { decodeSubscriptionPermissions } from '$logic/subscription'
import { ChatAppBarRoot } from './chat-app-bar'
import {
  useCurrentWorkspace,
  useIsClientUserWorkspaceOwner,
} from '$logic/workspace'
import { useConditionalHook } from '@weblite/use-conditional-hook'

interface Props {
  selectedItems: RoomListItem[]
}

const RowDirectionStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const RemoveIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  margin-right: 14px;
`

const CloseIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  margin-right: 22px;
`

export const ChatAppBarSelection = memo(function ChatAppBarSelectionComponent({
  selectedItems,
}: Props) {
  const currentWorkspace = useCurrentWorkspace()
  const onMenuClick = () => showChatListMenu({ chatItems: selectedItems })

  const isOneItemAndOneItemIsWorkspace =
    selectedItems.length === 1 && selectedItems[0].type === WorkspaceType

  const isCLientUserWorkspaceOwner =
    useConditionalHook(isOneItemAndOneItemIsWorkspace, {
      hook: useIsClientUserWorkspaceOwner,
      defaultValue: undefined,
      hookArgs: selectedItems[0].chatId,
    }) || false

  const isWorkspaceInSelectedItems = selectedItems.some(
    item => item.type === WorkspaceType,
  )

  const canDelete = isWorkspaceInSelectedItems
    ? isCLientUserWorkspaceOwner
    : selectedItems.length > 0

  const onDelete = useCallback(async () => {
    await exitRoomListFromSelectionMode()

    if (isWorkspaceInSelectedItems) {
      return showDeleteWorkspaceConfirmModal(selectedItems[0].chatId)
    }

    if (selectedItems.length > 1) {
      return showDeleteRoomsModal(R.pluck('chatId', selectedItems))
    }
    const room = await getRoom(selectedItems[0].chatId)
    if (!room) return undefined
    if (room?.type === 'PV') {
      const pv = await getPv(room.id)
      return showDeletePvModal(pv.to)
    }
    if (room?.type === 'WAPP_ROOM') {
      // TODO: wait for implementing showDeleteWappRoomModel
      return deleteWappRoom(room.id, false)
    }
    if (room?.type === 'PRIVATE_SPACE') {
      // TODO: wait for implementing showDeletePrivateSpaceModel
      return hidePrivateSpace(false)
    }
    const permissions = await decodeSubscriptionPermissions(room.id)
    if (permissions.isOwner) return showDeleteHallModal(room.id)
    return showLeaveHallModal(room.id)
  }, [selectedItems, isWorkspaceInSelectedItems])

  return (
    <ChatAppBarRoot backgroundColor={currentWorkspace?.color}>
      <RowDirectionStack>
        <IconButton onClick={onMenuClick}>
          <MenuIcon />
        </IconButton>
        {canDelete && (
          <RemoveIconButton onClick={onDelete}>
            <RemoveIcon sx={{ color: 'inherit' }} />
          </RemoveIconButton>
        )}
      </RowDirectionStack>

      <RowDirectionStack>
        <Typography variant="subtitle1" color="inherit">
          {toLocale(selectedItems.length)}
        </Typography>
        <CloseIconButton onClick={exitRoomListFromSelectionMode}>
          <CloseIcon sx={{ color: 'inherit' }} />
        </CloseIconButton>
      </RowDirectionStack>
    </ChatAppBarRoot>
  )
})
